import type { RegimeTva } from '@artosera/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
import type { OeuvrePresentee } from './oeuvres.js';

export interface ElementSelection {
  oeuvreId: string;
  exemplaireId: string | null;
}
export interface SelectionPresentation {
  id: string;
  titre: string;
  contactId: string | null;
  note: string;
  elements: ElementSelection[];
  origine: { type: 'oeuvres' | 'exposition' | 'contact' | 'duplication'; id: string | null };
  archivee: boolean;
  archiveeLe: string | null;
  version: number;
  creeeLe: string;
  modifieeLe: string;
}
export interface CorpsSelection {
  titre: string;
  contactId?: string | null;
  note?: string;
  elements?: readonly { oeuvreId: string; exemplaireId?: string | null }[];
  depuis?: { exposition: string } | { contact: string };
}
export interface ModificationSelection {
  titre?: string;
  contactId?: string | null;
  note?: string;
  version: number;
}
export interface InteretPresentation extends ElementSelection {
  interesse: boolean;
  marqueLe: string;
}
export interface ChargePresentation {
  selection: SelectionPresentation;
  elements: (ElementSelection & {
    oeuvre: Pick<
      OeuvrePresentee,
      | 'id'
      | 'titre'
      | 'annee'
      | 'technique'
      | 'dimensions'
      | 'type'
      | 'tirage'
      | 'statut'
      | 'prixPublicCentimes'
      | 'devise'
    >;
    exemplaire: {
      id: string;
      numero: string;
      statut: OeuvrePresentee['statut'];
      prixCentimes: number | null;
    } | null;
    artiste: { id: string; nom: string };
    imageUrl: string | null;
  })[];
  interets: InteretPresentation[];
}
export interface RecapitulatifPresentation {
  selection: SelectionPresentation;
  interets: InteretPresentation[];
  actions: { notes: boolean; offre: boolean; affaire: boolean; reservation: boolean };
}
export interface ReglagesNegociation {
  remiseMaxPointsDeBase: number;
  parArtiste: { artisteId: string; remiseMaxPointsDeBase: number }[];
  seuilApprobationPointsDeBase: number | null;
  version: number;
}
export type StatutApprobation = 'en_attente' | 'acceptee' | 'refusee' | 'expiree';
export interface ApprobationNegociation extends ElementSelection {
  id: string;
  contactId: string;
  selectionId: string | null;
  prixPublicCentimes: number;
  prixProposeCentimes: number;
  remisePointsDeBase: number;
  demandeur: { compteId: string | null; jetonId: string | null; email: string };
  demandeeLe: string;
  motifDemande: string;
  statut: StatutApprobation;
  decision: { par: string | null; jetonId: string | null; le: string; note: string } | null;
  expireLe: string | null;
}
export type EntreeCalcul = {
  oeuvreId: string;
  exemplaireId?: string | null;
  contactId?: string;
} & (
  | { prixProposeCentimes: number; remisePointsDeBase?: never; remiseCentimes?: never }
  | { remisePointsDeBase: number; prixProposeCentimes?: never; remiseCentimes?: never }
  | { remiseCentimes: number; prixProposeCentimes?: never; remisePointsDeBase?: never }
  | { prixProposeCentimes?: never; remisePointsDeBase?: never; remiseCentimes?: never }
);
export type DemandeApprobation = EntreeCalcul & {
  contactId: string;
  selectionId?: string | null;
  motifDemande?: string;
};
export interface CalculNegociation {
  prixPublicCentimes: number;
  prixProposeCentimes: number;
  remiseCentimes: number;
  remisePointsDeBase: number;
  totalHtCentimes: number;
  totalTvaCentimes: number;
  totalTtcCentimes: number;
  tvaSurMargeCentimes?: number;
  droitDeSuiteCentimes: number;
  droitDeSuiteAChargeAcheteur: boolean;
  regimeEffectif?: RegimeTva;
  netArtisteCentimes?: number | null;
  netGalerieCentimes?: number | null;
  plancherEffectifCentimes?: number;
  sousPlancher: boolean;
  approbationRequise: boolean;
  approbation: ApprobationNegociation | null;
}
export interface FiltresSelections {
  archivee?: boolean;
  contactId?: string;
  page?: number;
  parPage?: number;
}
export interface FiltresApprobations {
  statut?: StatutApprobation;
  contactId?: string;
  oeuvreId?: string;
  valides?: '1';
  page?: number;
  parPage?: number;
}
function recherche(filtres: FiltresSelections | FiltresApprobations): string {
  const params = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(filtres)) {
    if (valeur !== undefined) params.set(cle, String(valeur));
  }
  return `?${params}`;
}
const base = (galerie: string) => `/g/${encodeURIComponent(galerie)}`;
const selection = (galerie: string, id: string) =>
  `${base(galerie)}/selections/${encodeURIComponent(id)}`;
const negociation = (galerie: string) => `${base(galerie)}/negociation`;
const plancher = (galerie: string, id: string) =>
  `${base(galerie)}/oeuvres/${encodeURIComponent(id)}/plancher`;

export const presentationApi = {
  lister: (galerie: string, filtres: FiltresSelections = {}, appel: AppelApi = {}) =>
    api.get<{ selections: SelectionPresentation[]; pagination: Pagination }>(
      `${base(galerie)}/selections${recherche(filtres)}`,
      { galerie, ...appel },
    ),
  creer: (galerie: string, corps: CorpsSelection, appel: AppelApi = {}) =>
    api.post<{ selection: SelectionPresentation }>(`${base(galerie)}/selections`, corps, {
      galerie,
      ...appel,
    }),
  obtenir: (galerie: string, id: string, appel: AppelApi = {}) =>
    api.get<{ selection: SelectionPresentation }>(selection(galerie, id), { galerie, ...appel }),
  modifier: (galerie: string, id: string, corps: ModificationSelection, appel: AppelApi = {}) =>
    api.patch<{ selection: SelectionPresentation }>(selection(galerie, id), corps, {
      galerie,
      ...appel,
    }),
  remplacerElements: (
    galerie: string,
    id: string,
    corps: { elements: readonly ElementSelection[]; version: number },
    appel: AppelApi = {},
  ) =>
    api.put<{ selection: SelectionPresentation }>(`${selection(galerie, id)}/elements`, corps, {
      galerie,
      ...appel,
    }),
  dupliquer: (galerie: string, id: string, appel: AppelApi = {}) =>
    api.post<{ selection: SelectionPresentation }>(
      `${selection(galerie, id)}/duplication`,
      undefined,
      { galerie, ...appel },
    ),
  archiver: (galerie: string, id: string, archivee: boolean, appel: AppelApi = {}) => {
    const chemin = `${selection(galerie, id)}/archivage`;
    if (archivee)
      return api.post<{ selection: SelectionPresentation }>(chemin, undefined, {
        galerie,
        ...appel,
      });
    return api.delete<{ selection: SelectionPresentation }>(chemin, { galerie, ...appel });
  },
  presenter: (galerie: string, id: string, appel: AppelApi = {}) =>
    api.get<ChargePresentation>(`${selection(galerie, id)}/presentation`, { galerie, ...appel }),
  marquerInteret: (
    galerie: string,
    id: string,
    oeuvreId: string,
    corps: { exemplaireId?: string | null; interesse: boolean },
    appel: AppelApi = {},
  ) =>
    api.put<{ interet: InteretPresentation }>(
      `${selection(galerie, id)}/interets/${encodeURIComponent(oeuvreId)}`,
      corps,
      { galerie, ...appel },
    ),
  recapitulatif: (galerie: string, id: string, appel: AppelApi = {}) =>
    api.get<RecapitulatifPresentation>(`${selection(galerie, id)}/recapitulatif`, {
      galerie,
      ...appel,
    }),
  noter: (galerie: string, id: string, corps: { note?: string }, appel: AppelApi = {}) =>
    api.post<{ contactId: string }>(`${selection(galerie, id)}/recapitulatif/notes`, corps, {
      galerie,
      ...appel,
    }),
  reglages: (galerie: string, appel: AppelApi = {}) =>
    api.get<{ reglages: ReglagesNegociation }>(`${negociation(galerie)}/reglages`, {
      galerie,
      ...appel,
    }),
  regler: (galerie: string, corps: ReglagesNegociation, appel: AppelApi = {}) =>
    api.put<{ reglages: ReglagesNegociation }>(`${negociation(galerie)}/reglages`, corps, {
      galerie,
      ...appel,
    }),
  plancher: (galerie: string, id: string, appel: AppelApi = {}) =>
    api.get<{ plancherCentimes: number | null }>(plancher(galerie, id), { galerie, ...appel }),
  fixerPlancher: (
    galerie: string,
    id: string,
    plancherCentimes: number | null,
    appel: AppelApi = {},
  ) => {
    if (plancherCentimes === null)
      return api.delete<{ plancherCentimes: number | null }>(plancher(galerie, id), {
        galerie,
        ...appel,
      });
    return api.put<{ plancherCentimes: number | null }>(
      plancher(galerie, id),
      { plancherCentimes },
      { galerie, ...appel },
    );
  },
  calculer: (galerie: string, corps: EntreeCalcul, appel: AppelApi = {}) =>
    api.post<{ calcul: CalculNegociation }>(`${negociation(galerie)}/calcul`, corps, {
      galerie,
      ...appel,
    }),
  demander: (galerie: string, corps: DemandeApprobation, appel: AppelApi = {}) =>
    api.post<{ approbation: ApprobationNegociation }>(
      `${negociation(galerie)}/approbations`,
      corps,
      { galerie, ...appel },
    ),
  approbations: (galerie: string, filtres: FiltresApprobations = {}, appel: AppelApi = {}) =>
    api.get<{ approbations: ApprobationNegociation[]; pagination: Pagination }>(
      `${negociation(galerie)}/approbations${recherche(filtres)}`,
      { galerie, ...appel },
    ),
  compteApprobations: (galerie: string, appel: AppelApi = {}) =>
    api.get<{ compte: number }>(`${negociation(galerie)}/approbations/compte`, {
      galerie,
      ...appel,
    }),
  decider: (
    galerie: string,
    id: string,
    corps: { decision: 'acceptee' | 'refusee'; note: string },
    appel: AppelApi = {},
  ) =>
    api.post<{ approbation: ApprobationNegociation }>(
      `${negociation(galerie)}/approbations/${encodeURIComponent(id)}/decision`,
      corps,
      { galerie, ...appel },
    ),
};

export const clesPresentation = {
  toutes: (galerie: string) => cleMetier(galerie, 'presentation'),
  liste: (galerie: string, filtres: FiltresSelections) =>
    cleMetier(galerie, 'presentation', 'liste', filtres),
  fiche: (galerie: string, id: string) => cleMetier(galerie, 'presentation', 'fiche', id),
  charge: (galerie: string, id: string) => cleMetier(galerie, 'presentation', 'charge', id),
  recapitulatif: (galerie: string, id: string) =>
    cleMetier(galerie, 'presentation', 'recapitulatif', id),
  reglages: (galerie: string) => cleMetier(galerie, 'presentation', 'reglages'),
  plancher: (galerie: string, id: string) => cleMetier(galerie, 'presentation', 'plancher', id),
  approbations: (galerie: string, filtres: FiltresApprobations) =>
    cleMetier(galerie, 'presentation', 'approbations', filtres),
  compte: (galerie: string) => cleMetier(galerie, 'presentation', 'compte'),
};
export function useSelections(galerie: string, filtres: FiltresSelections, enabled = true) {
  return useQuery({
    queryKey: clesPresentation.liste(galerie, filtres),
    queryFn: (contexte) => presentationApi.lister(galerie, filtres, appelDeRequete(contexte)),
    enabled,
  });
}
export function usePresentation(galerie: string, id: string, enabled = true) {
  return useQuery({
    queryKey: clesPresentation.charge(galerie, id),
    queryFn: (contexte) => presentationApi.presenter(galerie, id, appelDeRequete(contexte)),
    enabled,
  });
}
export function useReglagesNegociation(galerie: string, enabled = true) {
  return useQuery({
    queryKey: clesPresentation.reglages(galerie),
    queryFn: (contexte) => presentationApi.reglages(galerie, appelDeRequete(contexte)),
    enabled,
  });
}
export function usePlancher(galerie: string, id: string) {
  return useQuery({
    queryKey: clesPresentation.plancher(galerie, id),
    queryFn: (contexte) => presentationApi.plancher(galerie, id, appelDeRequete(contexte)),
  });
}
export function useApprobations(galerie: string, filtres: FiltresApprobations, enabled = true) {
  return useQuery({
    queryKey: clesPresentation.approbations(galerie, filtres),
    queryFn: (contexte) => presentationApi.approbations(galerie, filtres, appelDeRequete(contexte)),
    enabled,
  });
}
export function useCompteApprobations(galerie: string, enabled: boolean) {
  return useQuery({
    queryKey: clesPresentation.compte(galerie),
    queryFn: (contexte) => presentationApi.compteApprobations(galerie, appelDeRequete(contexte)),
    enabled,
  });
}
export function useCreerSelection(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: CorpsSelection, appel) => presentationApi.creer(galerie, corps, appel),
      onSuccess: () => client.invalidateQueries({ queryKey: clesPresentation.toutes(galerie) }),
    }),
  );
}
export type ActionSelection =
  | { type: 'modifier'; id: string; corps: ModificationSelection }
  | { type: 'elements'; id: string; elements: readonly ElementSelection[]; version: number }
  | { type: 'dupliquer'; id: string }
  | { type: 'archiver'; id: string; archivee: boolean };
export function useActionSelection(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (action: ActionSelection, appel) => {
        switch (action.type) {
          case 'modifier':
            return presentationApi.modifier(galerie, action.id, action.corps, appel);
          case 'elements':
            return presentationApi.remplacerElements(
              galerie,
              action.id,
              { elements: action.elements, version: action.version },
              appel,
            );
          case 'dupliquer':
            return presentationApi.dupliquer(galerie, action.id, appel);
          case 'archiver':
            return presentationApi.archiver(galerie, action.id, action.archivee, appel);
        }
      },
      onSuccess: ({ selection }, action) => {
        if (action.type !== 'dupliquer') {
          client.setQueryData(clesPresentation.fiche(galerie, selection.id), { selection });
          client.setQueryData<ChargePresentation>(
            clesPresentation.charge(galerie, selection.id),
            (charge) => (charge ? { ...charge, selection } : undefined),
          );
        }
        return client.invalidateQueries({ queryKey: clesPresentation.toutes(galerie) });
      },
    }),
  );
}
export function useReglerNegociation(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (corps: ReglagesNegociation, appel) =>
        presentationApi.regler(galerie, corps, appel),
      onSuccess: () => client.invalidateQueries({ queryKey: clesPresentation.reglages(galerie) }),
    }),
  );
}
export function useFixerPlancher(galerie: string, id: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (montant: number | null, appel) =>
        presentationApi.fixerPlancher(galerie, id, montant, appel),
      onSuccess: () => client.invalidateQueries({ queryKey: clesPresentation.toutes(galerie) }),
    }),
  );
}
export function useDeciderApprobation(galerie: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (
        { id, decision, note }: { id: string; decision: 'acceptee' | 'refusee'; note: string },
        appel,
      ) => presentationApi.decider(galerie, id, { decision, note }, appel),
      onSuccess: () => client.invalidateQueries({ queryKey: clesPresentation.toutes(galerie) }),
    }),
  );
}
